import Card from '@/components/card';
import FormSection from '@/components/form/form-section';
import PageLink from '@/components/page-link';
import Text from '@/components/text';
import { ClassifierSubType } from '@/lib/models/classifier';
import {
  ClassifierControlConfig,
  ClassifierControlsSettings,
  getClassifierControlActionOptions,
} from '@/lib/models/policy-model/classifier-controls';
import { ControlActionValue } from '@/lib/models/policy-model/controls';
import { FC, useCallback, useMemo } from 'react';
import {
  ClassifierControlListProps,
  getClassifierItemOptions,
  updateClassifierAction,
} from './helpers';
import Item from './item';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const IntentControlList: FC<ClassifierControlListProps> = ({
  formHandle,
  readonly,
  defaultPolicy,
  withNotSet = true,
  classifiers,
  refetch,
}) => {
  const { values, setFieldValue } = formHandle;
  const { classifierControls = new ClassifierControlConfig() } = values;
  const { entities = [] } = classifierControls;
  const defaultClassifierControls = defaultPolicy?.classifierControls;

  const intentClassifiers = useMemo(
    () => classifiers.filter(({ subtype }) => subtype === ClassifierSubType.Custom),
    [classifiers]
  );
  const hasClassifiers = !!intentClassifiers.length;

  const handleChange = useCallback(
    (classifierId: string, action: ControlActionValue) => {
      const updated = updateClassifierAction(entities, classifierId, action);
      setFieldValue('classifierControls.entities', updated);
    },
    [entities, setFieldValue]
  );

  const actionOptionsMap = useMemo(
    () => getClassifierControlActionOptions(intentClassifiers),
    [intentClassifiers]
  );

  const classifierSettings = useMemo(
    () => ClassifierControlsSettings(intentClassifiers),
    [intentClassifiers]
  );

  return (
    <FormSection title="Intent Topic Controls" scrollId="intent-controls">
      <FormSectionSubtitle>
        Choose what action is taken when a request matches an intent topic. Head over to{' '}
        <PageLink routeKey="classifiers" /> to create or manage intent topics.
      </FormSectionSubtitle>
      <Card>
        {!hasClassifiers ? (
          <Text italic>No custom intents found.</Text>
        ) : (
          classifierSettings.map(({ classifierId, label, description, enabled }) => {
            const entity = entities.find(({ classifierId: id }) => id === classifierId);
            const { action: defaultPolicyAction } =
              (defaultClassifierControls?.entities ?? []).find(
                ({ classifierId: id }) => id === classifierId
              ) || {};

            const options = getClassifierItemOptions(
              actionOptionsMap,
              classifierId,
              defaultPolicyAction
            );

            return (
              <Item
                key={`classifier-${classifierId}`}
                classifierId={classifierId}
                label={label}
                description={description}
                enabled={!!enabled}
                readonly={readonly}
                name={`classifierControls.${classifierId}`}
                options={options}
                value={entity?.action || ''}
                withNotSet={withNotSet}
                refetch={refetch}
                onChange={(value: string) =>
                  handleChange(classifierId, value as ControlActionValue)
                }
              />
            );
          })
        )}
      </Card>
    </FormSection>
  );
};

export default IntentControlList;
